import React, { useEffect, useMemo, useState } from 'react'
import Select, { StylesConfig } from 'react-select'
import {
    ArrowLeft,
    RefreshCw,
    Activity,
    Mic,
    Volume2,
    Waves,
    Accessibility,
    PanelLeft,
    PanelRight,
    SlidersHorizontal,
    Headphones,
} from 'lucide-react'
import type { VoiceEnhancementMode } from '../../types/amazon-connect-streams'
import type { AudioDeviceOption } from '../../hooks/useConnectStream'
import toaster from '../../Utils/toaster'
import { runCcpLocalDiagnostics } from '../../services/ccpLocalDiagnostics'
import { CALL_A11Y } from './callAccessibility'
import type { WidgetDockSide } from './widgetDockPreference'

interface DeviceOption {
    value: string
    label: string
}

interface ConnectWidgetSettingsProps {
    voiceEnhancementMode: VoiceEnhancementMode
    microphoneDeviceId: string | null
    speakerDeviceId: string | null
    microphones: AudioDeviceOption[]
    speakers: AudioDeviceOption[]
    isAudioEnhancementLocked: boolean
    dockSide: WidgetDockSide
    onDockSideChange: (side: WidgetDockSide) => void
    onBack: () => void
    onRefreshDevices: () => Promise<void>
    onSetVoiceEnhancementMode: (mode: VoiceEnhancementMode) => Promise<void>
    onSetMicrophoneDevice: (deviceId: string) => Promise<void>
    onSetSpeakerDevice: (deviceId: string) => Promise<void>
}

const ENHANCEMENT_OPTIONS: DeviceOption[] = [
    { value: 'NONE', label: 'Off' },
    { value: 'NOISE_SUPPRESSION', label: 'Noise suppression' },
    { value: 'VOICE_ISOLATION', label: 'Voice isolation' },
]

const settingsSelectStyles: StylesConfig<DeviceOption, false> = {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    control: (base, state) => ({
        ...base,
        minHeight: 32,
        height: 32,
        borderRadius: 8,
        borderColor: state.isFocused ? '#D7383C' : '#E5E7EB',
        boxShadow: state.isFocused ? '0 0 0 1px #D7383C' : 'none',
        backgroundColor: state.isDisabled ? '#F3F4F6' : '#fff',
        fontSize: 12,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        '&:hover': {
            borderColor: state.isFocused ? '#D7383C' : '#D1D5DB',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
        height: 30,
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: 12,
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: 30,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 6px',
        color: '#9CA3AF',
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: '0 4px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    placeholder: (base) => ({
        ...base,
        fontSize: 12,
        color: '#9CA3AF',
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: 12,
        color: '#111827',
        maxWidth: 'calc(100% - 8px)',
    }),
    menu: (base) => ({
        ...base,
        borderRadius: 8,
        overflow: 'hidden',
        fontSize: 12,
        zIndex: 10050,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 12,
        padding: '7px 10px',
        backgroundColor: state.isSelected
            ? '#D7383C'
            : state.isFocused
              ? '#FEF2F2'
              : '#fff',
        color: state.isSelected ? '#fff' : '#111827',
        cursor: 'pointer',
    }),
}

function toOptions(devices: AudioDeviceOption[]): DeviceOption[] {
    return devices.map((d) => ({ value: d.deviceId, label: d.label }))
}

export function ConnectWidgetSettings({
    voiceEnhancementMode,
    microphoneDeviceId,
    speakerDeviceId,
    microphones,
    speakers,
    isAudioEnhancementLocked,
    dockSide,
    onDockSideChange,
    onBack,
    onRefreshDevices,
    onSetVoiceEnhancementMode,
    onSetMicrophoneDevice,
    onSetSpeakerDevice,
}: ConnectWidgetSettingsProps) {
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [isDiagnosing, setIsDiagnosing] = useState(false)

    const micOptions = useMemo(() => toOptions(microphones), [microphones])
    const speakerOptions = useMemo(() => toOptions(speakers), [speakers])

    const selectedMic = micOptions.find((o) => o.value === microphoneDeviceId) || null
    const selectedSpeaker = speakerOptions.find((o) => o.value === speakerDeviceId) || null
    const selectedEnhancement =
        ENHANCEMENT_OPTIONS.find((o) => o.value === (voiceEnhancementMode || 'NONE')) ||
        ENHANCEMENT_OPTIONS[0]

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            setIsRefreshing(true)
            try {
                await onRefreshDevices()
            } finally {
                if (!cancelled) setIsRefreshing(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [onRefreshDevices])

    const handleRefresh = async () => {
        setIsRefreshing(true)
        try {
            await onRefreshDevices()
            toaster('success', 'Devices refreshed')
        } catch {
            toaster('error', 'Could not refresh devices')
        } finally {
            setIsRefreshing(false)
        }
    }

    const handleDiagnostics = async () => {
        if (isAudioEnhancementLocked) return
        setIsDiagnosing(true)
        try {
            const report = await runCcpLocalDiagnostics()
            for (const check of report.checks) {
                if (check.ok) {
                    toaster('success', check.message)
                } else if (check.id === 'ice' && check.message.startsWith('Skipped')) {
                    toaster('warning', check.message)
                } else if (check.message.includes('timed out')) {
                    toaster('warning', check.message)
                } else {
                    toaster('error', check.message)
                }
            }
        } catch {
            toaster('error', 'Diagnostics failed')
        } finally {
            setIsDiagnosing(false)
        }
    }

    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null

    return (
        <div className="cw-settings-screen h-100 d-flex flex-column">
            <div className="cw-settings-header d-flex align-items-center gap-2 px-3 py-2 border-bottom">
                <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-dark"
                    onClick={onBack}
                    aria-label="Back to call panel"
                >
                    <ArrowLeft size={16} aria-hidden="true" />
                </button>
                <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>Settings</h6>
            </div>

            <div className="cw-settings-body flex-grow-1 overflow-auto px-3 py-3">
                <section className="cw-settings-card cw-settings-card--compact" aria-labelledby="cw-pref-settings-title">
                    <div className="cw-settings-card-title" id="cw-pref-settings-title">
                        <SlidersHorizontal size={12} aria-hidden="true" />
                        Preferences
                    </div>
                    <div className="cw-settings-pref-row">
                        <label className="cw-settings-label mb-0" id="cw-dock-side-label">
                            Panel position
                        </label>
                        <div
                            className="cw-dock-toggle"
                            role="group"
                            aria-labelledby="cw-dock-side-label"
                        >
                            <button
                                type="button"
                                className={`cw-dock-toggle-btn${dockSide === 'left' ? ' is-active' : ''}`}
                                aria-pressed={dockSide === 'left'}
                                onClick={() => onDockSideChange('left')}
                            >
                                <PanelLeft size={14} aria-hidden="true" />
                                Left
                            </button>
                            <button
                                type="button"
                                className={`cw-dock-toggle-btn${dockSide === 'right' ? ' is-active' : ''}`}
                                aria-pressed={dockSide === 'right'}
                                onClick={() => onDockSideChange('right')}
                            >
                                <PanelRight size={14} aria-hidden="true" />
                                Right
                            </button>
                        </div>
                    </div>
                </section>

                <section className="cw-settings-card mt-3">
                    <div className="cw-settings-card-title">
                        <Headphones size={12} aria-hidden="true" />
                        Audio settings
                    </div>

                    <div className="cw-settings-field">
                        <label className="cw-settings-label">
                            <Mic size={12} />
                            Microphone
                        </label>
                        <Select<DeviceOption, false>
                            classNamePrefix="react-select"
                            className="react-select-styled react-select-solid react-select-sm"
                            styles={settingsSelectStyles}
                            options={micOptions}
                            value={selectedMic}
                            isLoading={isRefreshing}
                            isDisabled={micOptions.length === 0}
                            isSearchable
                            isClearable={false}
                            placeholder={isRefreshing ? 'Loading…' : 'No microphones found'}
                            noOptionsMessage={() => (isRefreshing ? 'Loading…' : 'No microphones found')}
                            menuPortalTarget={menuPortalTarget}
                            menuPlacement="auto"
                            onChange={async (opt: DeviceOption | null) => {
                                if (!opt?.value) return
                                try {
                                    await onSetMicrophoneDevice(opt.value)
                                } catch {
                                    toaster('error', 'Could not update microphone')
                                }
                            }}
                            aria-label="Microphone device"
                        />
                    </div>

                    <div className="cw-settings-field">
                        <label className="cw-settings-label">
                            <Volume2 size={12} />
                            Speaker
                        </label>
                        <Select<DeviceOption, false>
                            classNamePrefix="react-select"
                            className="react-select-styled react-select-solid react-select-sm"
                            styles={settingsSelectStyles}
                            options={speakerOptions}
                            value={selectedSpeaker}
                            isLoading={isRefreshing}
                            isDisabled={speakerOptions.length === 0}
                            isSearchable
                            isClearable={false}
                            placeholder={isRefreshing ? 'Loading…' : 'No speakers found'}
                            noOptionsMessage={() => (isRefreshing ? 'Loading…' : 'No speakers found')}
                            menuPortalTarget={menuPortalTarget}
                            menuPlacement="auto"
                            onChange={async (opt: DeviceOption | null) => {
                                if (!opt?.value) return
                                try {
                                    await onSetSpeakerDevice(opt.value)
                                } catch {
                                    toaster('error', 'Could not update speaker')
                                }
                            }}
                            aria-label="Speaker device"
                        />
                    </div>

                    <div className="cw-settings-field">
                        <label className="cw-settings-label">
                            <Waves size={12} />
                            Audio enhancement
                        </label>
                        <Select<DeviceOption, false>
                            classNamePrefix="react-select"
                            className="react-select-styled react-select-solid react-select-sm"
                            styles={settingsSelectStyles}
                            options={ENHANCEMENT_OPTIONS}
                            value={selectedEnhancement}
                            isDisabled={isAudioEnhancementLocked}
                            isSearchable={false}
                            isClearable={false}
                            menuPortalTarget={menuPortalTarget}
                            menuPlacement="auto"
                            onChange={async (opt: DeviceOption | null) => {
                                if (!opt?.value) return
                                try {
                                    await onSetVoiceEnhancementMode(opt.value as VoiceEnhancementMode)
                                    toaster('success', 'Saved — applies on next call')
                                } catch {
                                    toaster('error', 'Could not update audio enhancement')
                                }
                            }}
                            aria-label="Audio enhancement mode"
                        />
                        <p className="cw-settings-hint">
                            {isAudioEnhancementLocked
                                ? 'Locked during a call.'
                                : 'Applies on the next call. Voice isolation is CPU-heavy.'}
                        </p>
                    </div>

                    <button
                        type="button"
                        className="cw-settings-btn"
                        disabled={isRefreshing}
                        onClick={() => void handleRefresh()}
                        aria-label={isRefreshing ? 'Refreshing audio devices' : 'Refresh audio devices'}
                    >
                        <RefreshCw size={13} className={isRefreshing ? 'cw-spin' : undefined} aria-hidden="true" />
                        {isRefreshing ? 'Refreshing…' : 'Refresh devices'}
                    </button>
                </section>

                <section className="cw-settings-card mt-3">
                    <div className="cw-settings-card-title">
                        <Activity size={12} aria-hidden="true" />
                        Run diagnostics
                    </div>
                    <p className="cw-settings-hint mb-2">
                        {isAudioEnhancementLocked
                            ? 'Unavailable during an active call.'
                            : 'Checks mic permission and Amazon Connect softphone STUN (UDP 3478).'}
                    </p>
                    <button
                        type="button"
                        className="cw-settings-btn cw-settings-btn-primary"
                        disabled={isDiagnosing || isAudioEnhancementLocked}
                        onClick={() => void handleDiagnostics()}
                        aria-label={
                            isAudioEnhancementLocked
                                ? 'Diagnostics unavailable during a call'
                                : isDiagnosing
                                    ? 'Running microphone and network diagnostics'
                                    : 'Run microphone and network diagnostics'
                        }
                    >
                        {isDiagnosing ? (
                            <RefreshCw size={13} className="cw-spin" aria-hidden="true" />
                        ) : (
                            <Activity size={13} aria-hidden="true" />
                        )}
                        {isDiagnosing ? 'Running…' : 'Mic + network check'}
                    </button>
                </section>

                <section className="cw-settings-card mt-3" aria-labelledby="cw-a11y-settings-title">
                    <div className="cw-settings-card-title" id="cw-a11y-settings-title">
                        <Accessibility size={12} aria-hidden="true" />
                        Accessibility
                    </div>
                    <p className="cw-settings-hint mb-2">
                        Incoming calls are announced to screen readers. Accept focuses the Connect panel.
                    </p>
                    <ul className="cw-settings-a11y-list mb-2">
                        <li>
                            Accept call: <kbd>{CALL_A11Y.acceptHint}</kbd>
                        </li>
                        <li>
                            Decline call: <kbd>{CALL_A11Y.declineHint}</kbd>
                        </li>
                    </ul>
                    <p className="cw-settings-hint mb-2">During an active call:</p>
                    <ul className="cw-settings-a11y-list mb-0">
                        <li>
                            Mute: <kbd>{CALL_A11Y.muteHint}</kbd>
                        </li>
                        <li>
                            Hold or resume all: <kbd>{CALL_A11Y.holdHint}</kbd>
                        </li>
                        <li>
                            Conference: <kbd>{CALL_A11Y.conferenceHint}</kbd>
                        </li>
                        <li>
                            Keypad: <kbd>{CALL_A11Y.keypadHint}</kbd>
                        </li>
                        <li>
                            Call info: <kbd>{CALL_A11Y.callInfoHint}</kbd>
                        </li>
                        <li>
                            End call: <kbd>{CALL_A11Y.endCallHint}</kbd>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    )
}
